
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { getPwaInstallStats } from '@/utils/pwaTracking';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { formatDistanceToNow } from 'date-fns';
import { Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

export const PwaInstallStats = () => {
  const { data: stats, isLoading, error, refetch } = useQuery({
    queryKey: ['pwaInstallStats'],
    queryFn: getPwaInstallStats,
  });

  if (isLoading) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle>PWA Installation Statistics</CardTitle>
          <CardDescription>Track how many users have installed your website as an app.</CardDescription>
        </CardHeader>
        <CardContent className="flex justify-center items-center h-32">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle>PWA Installation Statistics</CardTitle>
          <CardDescription>Track how many users have installed your website as an app.</CardDescription>
        </CardHeader>
        <CardContent className="text-red-600 flex flex-col items-center">
          <AlertCircle className="h-8 w-8 mb-2" />
          <p>Error loading PWA statistics: {error.message}</p>
          <Button onClick={() => refetch()} className="mt-4">Try Again</Button>
        </CardContent>
      </Card>
    );
  }
  
  const totalInstalls = stats?.totalInstalls || 0;
  const distinctDeviceInstalls = stats?.distinctDeviceInstalls || 0;
  const lastInstalled = stats?.lastInstalled;
  const events = stats?.events || [];

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>PWA Installation Statistics</CardTitle>
        <CardDescription>
          Track how many users have installed your website as an app. Data from all users.
          <Button onClick={() => refetch()} size="sm" className="ml-4">Refresh</Button>
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 mb-6">
          <div className="rounded-lg bg-green-50 dark:bg-green-900/20 p-4">
            <h3 className="text-sm font-medium text-green-800 dark:text-green-300">Total App Installations (Events)</h3>
            <div className="mt-2 text-2xl font-semibold">{totalInstalls}</div>
          </div>
          <div className="rounded-lg bg-purple-50 dark:bg-purple-900/20 p-4">
            <h3 className="text-sm font-medium text-purple-800 dark:text-purple-300">Distinct Device Installations</h3>
            <div className="mt-2 text-2xl font-semibold">{distinctDeviceInstalls}</div>
             <p className="text-xs text-gray-500 mt-1">Based on unique device IDs that triggered an install event.</p>
          </div>
          <div className="rounded-lg bg-blue-50 dark:bg-blue-900/20 p-4">
            <h3 className="text-sm font-medium text-blue-800 dark:text-blue-300">Last Installation</h3>
            <div className="mt-2 text-sm">
              {lastInstalled ? (
                <>
                  {formatDistanceToNow(new Date(lastInstalled), { addSuffix: true })}
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {new Date(lastInstalled).toLocaleString()}
                  </div>
                </>
              ) : (
                "No installations recorded"
              )}
            </div>
          </div>
        </div>
        
        {events.length > 0 && (
          <div className="mt-6">
            <h3 className="text-sm font-medium mb-2">Recent PWA Events (Last 10)</h3>
            <div className="border rounded-md max-h-96 overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Event</TableHead>
                    <TableHead>Timestamp</TableHead>
                    <TableHead>Device ID (truncated)</TableHead>
                    <TableHead>User ID (truncated)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {events.map((event, index) => (
                    <TableRow key={index}>
                      <TableCell className="font-medium">{event.event_name}</TableCell>
                      <TableCell>
                        {formatDistanceToNow(new Date(event.created_at), { addSuffix: true })}
                      </TableCell>
                       <TableCell className="font-mono text-xs">
                        {event.device_id ? `${event.device_id.substring(0, 8)}...` : 'N/A'}
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {event.user_id ? `${event.user_id.substring(0, 8)}...` : 'Anonymous'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PwaInstallStats;
